import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import {
  FileSpreadsheet, FileDown, Loader2, ArrowUp, ArrowDown, Minus, Star, Users, Clock, MessageCircleWarning, Bot, UserCheck,
  ShieldCheck, Search,
} from 'lucide-react';
import { whatsappAPI } from './whatsappAPI';
import { useOrganizacao, Chip } from './organizacao';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { empresaAPI, getEmpresaPublicInfo } from '../../lib/api';
import { gerarRelatorioAtendimentoPDF, fmtDur } from './relatorioAtendimentoPDF';

/* Painel do atendimento (rota /whatsapp/analytics, mesma permissão de antes) e auditoria. */

const hojeLocal = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const somaDias = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const PRESETS = [
  ['hoje', 'Hoje', () => [hojeLocal(), hojeLocal()]],
  ['7', '7 dias', () => [somaDias(hojeLocal(), -6), hojeLocal()]],
  ['30', '30 dias', () => [somaDias(hojeLocal(), -29), hojeLocal()]],
  ['mes', 'Este mês', () => [`${hojeLocal().slice(0, 8)}01`, hojeLocal()]],
];
const num = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR'));
const diaCurto = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const desde = (iso) => (iso ? fmtDur((Date.now() - Date.parse(iso)) / 1000) : null);
// cores validadas (claro e escuro) para as duas séries de mensagens; "enviadas" também é tracejada
const CORES = { claro: { a: '#047857', b: '#4f46e5', grade: '#e2e8f0', eixo: '#64748b' }, escuro: { a: '#059669', b: '#6366f1', grade: '#1e293b', eixo: '#94a3b8' } };

function Variacao({ atual, antes, menorMelhor, neutro, formatar = num }) {
  if (atual == null || antes == null || antes === 0 && atual === 0) return <span className="text-xs text-slate-400">sem comparação</span>;
  if (antes === 0) return <span className="text-xs text-slate-500">antes: {formatar(antes)}</span>;
  const pct = Math.round(((atual - antes) / antes) * 100);
  if (pct === 0) return <span className="text-xs text-slate-500 flex items-center gap-0.5"><Minus className="w-3 h-3" />igual ao período anterior</span>;
  const melhor = menorMelhor ? pct < 0 : pct > 0;
  const Ic = pct > 0 ? ArrowUp : ArrowDown;
  if (neutro) {   // volume não é bom nem ruim por si só
    return <span className="text-xs text-slate-500 flex items-center gap-0.5" title={`Período anterior: ${formatar(antes)}`}><Ic className="w-3 h-3" />{Math.abs(pct)}% em relação ao período anterior</span>;
  }
  return (
    <span className={`text-xs font-medium flex items-center gap-0.5 ${melhor ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}
      title={`Período anterior: ${formatar(antes)}`}>
      <Ic className="w-3 h-3" />{Math.abs(pct)}% <span className="font-normal text-slate-500">({melhor ? 'melhor' : 'pior'})</span>
    </span>
  );
}

function Indicador({ titulo, valor, extra, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid={testid}>
      <p className="text-[13px] text-slate-500 dark:text-slate-400">{titulo}</p>
      <p className="text-2xl font-semibold tabular-nums mt-1">{valor}{extra && <span className="text-sm font-normal text-slate-500 ml-1">{extra}</span>}</p>
      <div className="mt-1 min-h-[18px]">{children}</div>
    </div>
  );
}

function Agora({ icon: Icon, rotulo, valor, detalhe, alerta, testid }) {
  return (
    <div className="flex items-center gap-3 min-w-[170px]" data-testid={testid}>
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${alerta ? 'bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300'}`}>
        <Icon className="w-[18px] h-[18px]" />
      </span>
      <span>
        <span className="text-xl font-semibold tabular-nums block leading-tight">{valor}</span>
        <span className="text-xs text-slate-500">{rotulo}{detalhe ? `, ${detalhe}` : ''}</span>
      </span>
    </div>
  );
}

function CaixaGrafico({ titulo, sub, children, testid }) {
  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid={testid}>
      <h3 className="text-sm font-semibold">{titulo}</h3>
      {sub && <p className="text-xs text-slate-500 mb-2">{sub}</p>}
      <div className="h-56 mt-2">{children}</div>
    </section>
  );
}

function DicaGrafico({ active, payload, label, rotulo }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
      <p className="font-medium mb-1">{rotulo ? rotulo(label) : label}</p>
      {payload.map(p => (
        <p key={p.dataKey} className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />{p.name}: <b className="text-slate-900 dark:text-white tabular-nums">{num(p.value)}</b>
        </p>
      ))}
    </div>
  );
}

export default function AnalyticsWA() {
  const { isAdmin, user } = useAuth();
  const admin = typeof isAdmin === 'function' ? isAdmin() : !!isAdmin;
  const [aba, setAba] = useState('painel');
  return (
    <div className="space-y-4" data-testid="wa-analytics-page">
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800">
        {[['painel', 'Painel e relatórios'], ...(admin ? [['auditoria', 'Auditoria']] : [])].map(([k, l]) => (
          <button key={k} onClick={() => setAba(k)} data-testid={`wa-painel-aba-${k}`}
            className={`pb-2.5 px-1 mr-3 -mb-px border-b-2 text-sm font-medium ${aba === k ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>{l}</button>
        ))}
      </div>
      {aba === 'painel' ? <Painel user={user} /> : <Auditoria />}
    </div>
  );
}

function Painel({ user }) {
  const { theme } = useTheme();
  const cor = theme === 'dark' ? CORES.escuro : CORES.claro;
  const { setores } = useOrganizacao();
  const [preset, setPreset] = useState('7');
  const [periodo, setPeriodo] = useState(PRESETS[1][2]());
  const [setor, setSetor] = useState('');
  const [d, setD] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(null);

  const params = useMemo(() => ({ inicio: periodo[0], fim: periodo[1], setor }), [periodo, setor]);
  const carregar = useCallback(async () => {
    setCarregando(true);
    try { const { data } = await whatsappAPI.painelExecutivo(params); setD(data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Não foi possível carregar o painel'); }
    finally { setCarregando(false); }
  }, [params]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { const t = setInterval(() => { if (!document.hidden) carregar(); }, 60000); return () => clearInterval(t); }, [carregar]);

  const escolherPreset = (k) => { setPreset(k); setPeriodo(PRESETS.find(p => p[0] === k)[2]()); };
  const setorNome = setor === 'sem' ? 'Sem setor' : setores.find(s => s.id === setor)?.nome;

  const baixarExcel = async () => {
    setExportando('excel');
    try {
      const { data } = await whatsappAPI.painelExcel(params);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a'); a.href = url; a.download = `atendimento_${periodo[0]}_a_${periodo[1]}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (_) { toast.error('Não foi possível gerar o Excel'); }
    finally { setExportando(null); }
  };
  const baixarPdf = async () => {
    if (!d) return;
    setExportando('pdf');
    try {
      let empresa = null;
      try { empresa = (await empresaAPI.getConfig()).data; } catch (_) { empresa = await getEmpresaPublicInfo().catch(() => null); }
      await gerarRelatorioAtendimentoPDF({ dados: d, empresa, setorNome, geradoPor: user?.nome });
    } catch (e) { console.error(e); toast.error('Não foi possível gerar o PDF'); }
    finally { setExportando(null); }
  };

  const r = d?.resumo; const a = d?.anterior; const ag = d?.agora;
  const maxFin = Math.max(1, ...(d?.atendentes || []).map(x => x.finalizados));
  const maxSet = Math.max(1, ...(d?.setores || []).map(x => x.atendimentos));
  const inputCls = 'h-9 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm outline-none';

  return (
    <div className={`space-y-4 transition-opacity ${carregando && d ? 'opacity-70' : ''}`}>
      {/* filtros numa linha só, acima de tudo */}
      <div className="flex flex-wrap items-center gap-2" data-testid="wa-painel-filtros">
        <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 text-sm">
          {PRESETS.map(([k, l]) => (
            <button key={k} onClick={() => escolherPreset(k)} data-testid={`wa-painel-preset-${k}`}
              className={`h-8 px-3 rounded-md font-medium ${preset === k ? 'bg-white dark:bg-slate-900 shadow-sm text-emerald-700 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-300'}`}>{l}</button>
          ))}
        </div>
        <input type="date" value={periodo[0]} max={periodo[1]} onChange={e => { setPreset(''); setPeriodo([e.target.value, periodo[1]]); }} className={inputCls} aria-label="Início" data-testid="wa-painel-inicio" />
        <span className="text-sm text-slate-500">até</span>
        <input type="date" value={periodo[1]} min={periodo[0]} max={hojeLocal()} onChange={e => { setPreset(''); setPeriodo([periodo[0], e.target.value]); }} className={inputCls} aria-label="Fim" />
        {setores.length > 0 && (
          <select value={setor} onChange={e => setSetor(e.target.value)} className={inputCls} data-testid="wa-painel-setor">
            <option value="">Todos os setores</option>
            {setores.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            <option value="sem">Sem setor</option>
          </select>
        )}
        <span className="flex-1" />
        <button onClick={baixarExcel} disabled={!d || !!exportando} data-testid="wa-painel-excel"
          className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50">
          {exportando === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-emerald-700" />} Excel
        </button>
        <button onClick={baixarPdf} disabled={!d || !!exportando} data-testid="wa-painel-pdf"
          className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50">
          {exportando === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4 text-red-600" />} PDF
        </button>
      </div>

      {!d ? <div className="py-20 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div> : (
        <>
          {/* agora */}
          <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4" data-testid="wa-painel-agora">
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex w-2 h-2"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 motion-safe:animate-ping" /><span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" /></span>
              <h2 className="text-sm font-semibold">Agora</h2>
              <span className="text-xs text-slate-500">atualiza a cada minuto</span>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-4">
              <Agora icon={UserCheck} rotulo="em atendimento" valor={ag.em_atendimento} testid="wa-agora-em" />
              <Agora icon={Clock} rotulo="na fila" valor={ag.na_fila} detalhe={ag.fila_mais_antiga ? `a mais antiga há ${desde(ag.fila_mais_antiga)}` : null} alerta={ag.na_fila > 0 && (Date.now() - Date.parse(ag.fila_mais_antiga)) > 15 * 60000} testid="wa-agora-fila" />
              <Agora icon={MessageCircleWarning} rotulo="clientes sem resposta" valor={ag.aguardando_resposta} alerta={ag.aguardando_resposta > 0} testid="wa-agora-sem-resposta" />
              <Agora icon={Bot} rotulo="com o assistente" valor={ag.no_assistente} />
              <Agora icon={Users} rotulo="atendentes online" valor={ag.atendentes_online} />
            </div>
          </section>

          {/* período */}
          <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-3">
            <Indicador titulo="Atendimentos finalizados" valor={num(r.finalizados)} extra={r.arquivados ? `+${r.arquivados} arquivados` : null} testid="wa-kpi-finalizados">
              <Variacao atual={r.finalizados} antes={a.finalizados} />
            </Indicador>
            {d.leads_disponivel && (
              <Indicador titulo="Leads novos" valor={num(r.leads_novos)} testid="wa-kpi-leads"><Variacao atual={r.leads_novos} antes={a.leads_novos} /></Indicador>
            )}
            <Indicador titulo="1ª resposta, em média" valor={fmtDur(r.primeira_resposta_media)} testid="wa-kpi-primeira">
              <Variacao atual={r.primeira_resposta_media} antes={a.primeira_resposta_media} menorMelhor formatar={fmtDur} />
            </Indicador>
            <Indicador titulo="Resolução, em média" valor={fmtDur(r.resolucao_media)} testid="wa-kpi-resolucao">
              <Variacao atual={r.resolucao_media} antes={a.resolucao_media} menorMelhor formatar={fmtDur} />
            </Indicador>
            <Indicador titulo="Avaliação dos clientes" valor={r.avaliacao_media != null ? r.avaliacao_media.toFixed(1).replace('.', ',') : '—'} extra={r.avaliacoes ? `de 5, ${r.avaliacoes} notas` : null} testid="wa-kpi-avaliacao">
              <Variacao atual={r.avaliacao_media} antes={a.avaliacao_media} formatar={v => v?.toFixed(1)} />
            </Indicador>
            <Indicador titulo="Mensagens recebidas" valor={num(r.mensagens_recebidas)} extra={`${num(r.mensagens_enviadas)} enviadas`} testid="wa-kpi-mensagens">
              <Variacao atual={r.mensagens_recebidas} antes={a.mensagens_recebidas} neutro />
            </Indicador>
          </div>

          <div className="grid lg:grid-cols-2 gap-3">
            <CaixaGrafico titulo="Atendimentos encerrados por dia" testid="wa-graf-atendimentos">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.por_dia} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={cor.grade} />
                  <XAxis dataKey="data" tickFormatter={diaCurto} tick={{ fontSize: 11, fill: cor.eixo }} axisLine={false} tickLine={false} minTickGap={12} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: cor.eixo }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: cor.grade, opacity: 0.5 }} content={<DicaGrafico rotulo={diaCurto} />} />
                  <Bar dataKey="atendimentos" name="Atendimentos" fill={cor.a} radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </CaixaGrafico>
            <CaixaGrafico titulo="Mensagens por dia" testid="wa-graf-mensagens">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.por_dia} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={cor.grade} />
                  <XAxis dataKey="data" tickFormatter={diaCurto} tick={{ fontSize: 11, fill: cor.eixo }} axisLine={false} tickLine={false} minTickGap={12} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: cor.eixo }} axisLine={false} tickLine={false} />
                  <Tooltip content={<DicaGrafico rotulo={diaCurto} />} cursor={{ stroke: cor.eixo, strokeDasharray: '3 3' }} />
                  <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="recebidas" name="Recebidas" stroke={cor.a} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="enviadas" name="Enviadas pela equipe" stroke={cor.b} strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CaixaGrafico>
          </div>

          <div className="grid xl:grid-cols-[1fr_1.4fr] gap-3">
            <CaixaGrafico titulo="Quando os clientes escrevem" sub="Mensagens recebidas por hora do dia, no período" testid="wa-graf-hora">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.por_hora} margin={{ top: 4, right: 8, left: -18, bottom: 0 }} barCategoryGap={2}>
                  <CartesianGrid vertical={false} stroke={cor.grade} />
                  <XAxis dataKey="hora" tickFormatter={h => `${h}h`} interval={2} tick={{ fontSize: 11, fill: cor.eixo }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: cor.eixo }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: cor.grade, opacity: 0.5 }} content={<DicaGrafico rotulo={h => `${h}h às ${h + 1}h`} />} />
                  <Bar dataKey="recebidas" name="Recebidas" fill={cor.a} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CaixaGrafico>

            <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 overflow-x-auto" data-testid="wa-painel-ranking">
              <h3 className="text-sm font-semibold mb-2">Atendentes</h3>
              {!d.atendentes.length ? <p className="text-sm text-slate-500 py-8 text-center">Nenhum atendimento encerrado no período.</p> : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 text-left">
                    <tr><th className="font-medium py-1.5 w-8">#</th><th className="font-medium py-1.5">Atendente</th><th className="font-medium py-1.5">Finalizados</th>
                      <th className="font-medium py-1.5 text-right">1ª resposta</th><th className="font-medium py-1.5 text-right">Duração</th>
                      <th className="font-medium py-1.5 text-right">Nota</th><th className="font-medium py-1.5 text-right">Agora</th></tr>
                  </thead>
                  <tbody>
                    {d.atendentes.map(t => {
                      const carga = ag.carga_atendentes.find(c => c.atendente_id === t.atendente_id)?.conversas || 0;
                      return (
                        <tr key={t.atendente_id || t.nome} className="border-t border-slate-100 dark:border-slate-800">
                          <td className="py-2 font-semibold tabular-nums text-slate-500">{t.posicao}</td>
                          <td className="py-2 font-medium">{t.nome}</td>
                          <td className="py-2 w-[32%]">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(t.finalizados / maxFin) * 100}%`, background: cor.a }} /></div>
                              <span className="tabular-nums w-8 text-right">{t.finalizados}</span>
                            </div>
                          </td>
                          <td className="py-2 text-right tabular-nums">{fmtDur(t.primeira_resposta_media)}</td>
                          <td className="py-2 text-right tabular-nums">{fmtDur(t.duracao_media)}</td>
                          <td className="py-2 text-right tabular-nums">
                            {t.avaliacao_media != null ? <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{t.avaliacao_media.toFixed(1).replace('.', ',')}</span> : '—'}
                          </td>
                          <td className="py-2 text-right tabular-nums text-slate-500" title="Conversas abertas com o atendente agora">{carga || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 overflow-x-auto" data-testid="wa-painel-setores">
            <h3 className="text-sm font-semibold mb-2">Setores</h3>
            {!d.setores.length ? <p className="text-sm text-slate-500 py-6 text-center">Nenhum atendimento encerrado no período.</p> : (
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 text-left">
                  <tr><th className="font-medium py-1.5">Setor</th><th className="font-medium py-1.5">Atendimentos</th><th className="font-medium py-1.5 text-right">Espera na fila</th>
                    <th className="font-medium py-1.5 text-right">1ª resposta</th><th className="font-medium py-1.5 text-right">Resolução</th>
                    <th className="font-medium py-1.5 text-right">Nota</th><th className="font-medium py-1.5 text-right">Na fila agora</th></tr>
                </thead>
                <tbody>
                  {d.setores.map(s => (
                    <tr key={s.setor_id || 'sem'} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-2">{s.cor ? <Chip cor={s.cor}>{s.nome}</Chip> : <span className="text-slate-500">{s.nome}</span>}</td>
                      <td className="py-2 w-[28%]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(s.atendimentos / maxSet) * 100}%`, background: cor.a }} /></div>
                          <span className="tabular-nums w-8 text-right">{s.atendimentos}</span>
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">{fmtDur(s.espera_media)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtDur(s.primeira_resposta_media)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtDur(s.resolucao_media)}</td>
                      <td className="py-2 text-right tabular-nums">{s.avaliacao_media != null ? s.avaliacao_media.toFixed(1).replace('.', ',') : '—'}</td>
                      <td className="py-2 text-right tabular-nums text-slate-500">{ag.fila_por_setor[s.setor_id || 'sem'] || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <p className="text-xs text-slate-500">
            Tempos de 1ª resposta, espera e resolução começaram a ser gravados na versão de setores e etiquetas; atendimentos mais antigos entram nas contagens, mas não nas médias.
          </p>
        </>
      )}
    </div>
  );
}

function Auditoria() {
  const [f, setF] = useState({ inicio: somaDias(hojeLocal(), -6), fim: hojeLocal(), usuario: '', acao: '', q: '', incluir_mensagens: false });
  const [qDig, setQDig] = useState('');
  const [pagina, setPagina] = useState(1);
  const [d, setD] = useState(null);
  const [carregando, setCarregando] = useState(false);
  useEffect(() => { const t = setTimeout(() => { setF(x => ({ ...x, q: qDig })); setPagina(1); }, 300); return () => clearTimeout(t); }, [qDig]);
  useEffect(() => {
    let vivo = true; setCarregando(true);
    whatsappAPI.auditoria({ ...f, pagina }).then(r => vivo && setD(r.data)).catch(e => toast.error(e?.response?.data?.detail || 'Não foi possível carregar a auditoria'))
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
  }, [f, pagina]);
  const set = (k) => (e) => { setF(x => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })); setPagina(1); };
  const inputCls = 'h-9 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm outline-none';
  const grupos = useMemo(() => {
    const g = [];
    (d?.itens || []).forEach(i => {
      const dia = new Date(i.at).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
      if (!g.length || g[g.length - 1].dia !== dia) g.push({ dia, itens: [] });
      g[g.length - 1].itens.push(i);
    });
    return g;
  }, [d]);
  const detalhe = (i) => {
    const x = i.detalhe || {};
    const partes = [];
    if (x.para) partes.push(`para ${x.para}`);
    if (x.para_setor) partes.push(`setor ${x.para_setor}`);
    if (x.situacao) partes.push(x.situacao.replaceAll('_', ' '));
    if (x.colocou) partes.push(`colocou: ${x.colocou}`);
    if (x.tirou) partes.push(`tirou: ${x.tirou}`);
    if (x.setor) partes.push(x.setor);
    if (x.etiqueta) partes.push(x.etiqueta);
    if (x.antes) partes.push(`antes: ${x.antes}`);
    if (x.palavras) partes.push(`“${x.palavras}”`);
    if (x.campos) partes.push(`campos: ${x.campos}`);
    if (x.motivo) partes.push(`motivo: ${x.motivo}`);
    if (x.nota) partes.push(`recado: ${x.nota}`);
    if (x.protocolo) partes.push(`#${x.protocolo}`);
    if (x.descricao) partes.push(x.descricao);
    if (x.texto) partes.push(`“${x.texto}”`);
    return partes.join(' · ');
  };
  const totalPag = d ? Math.max(1, Math.ceil(d.total / d.por_pagina)) : 1;

  return (
    <div className="space-y-3" data-testid="wa-auditoria">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={f.inicio} max={f.fim} onChange={set('inicio')} className={inputCls} aria-label="Início" />
        <span className="text-sm text-slate-500">até</span>
        <input type="date" value={f.fim} min={f.inicio} onChange={set('fim')} className={inputCls} aria-label="Fim" />
        <select value={f.usuario} onChange={set('usuario')} className={inputCls} data-testid="wa-aud-usuario">
          <option value="">Todas as pessoas</option>{(d?.usuarios || []).map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={f.acao} onChange={set('acao')} className={inputCls} data-testid="wa-aud-acao">
          <option value="">Todas as ações</option>
          {Object.entries(d?.acoes || {}).filter(([k]) => !['qr'].includes(k)).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input value={qDig} onChange={e => setQDig(e.target.value)} placeholder="Contato, pessoa ou detalhe" className="w-full h-9 pl-9 pr-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none" />
        </div>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={f.incluir_mensagens} onChange={set('incluir_mensagens')} className="w-4 h-4 accent-emerald-600" data-testid="wa-aud-mensagens" /> Incluir mensagens enviadas
        </label>
      </div>
      <div className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 ${carregando ? 'opacity-70' : ''}`}>
        {!d ? <div className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : !d.itens.length ? <p className="py-14 text-center text-sm text-slate-500">Nenhuma ação registrada com esses filtros.</p> : grupos.map(g => (
            <div key={g.dia}>
              <p className="px-4 py-2 text-xs font-semibold text-slate-500 bg-slate-50 dark:bg-slate-800/50 first-letter:uppercase border-b border-slate-100 dark:border-slate-800">{g.dia}</p>
              <ul>
                {g.itens.map((i, k) => (
                  <li key={`${i.at}-${k}`} className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0 flex items-start gap-3 text-sm" data-testid="wa-aud-item">
                    <span className="text-xs text-slate-500 tabular-nums w-11 shrink-0 pt-0.5">{new Date(i.at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                    <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    <span className="min-w-0">
                      <span><b className="font-semibold">{i.por}</b> {i.acao_nome.charAt(0).toLowerCase() + i.acao_nome.slice(1)}{i.conversa ? <> com <b className="font-medium">{i.conversa}</b></> : null}</span>
                      {detalhe(i) && <span className="block text-xs text-slate-500 break-words">{detalhe(i)}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </div>
      {d && d.total > d.por_pagina && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>{d.total} registros</span>
          <div className="flex gap-1">
            <button disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)} className="h-8 px-3 rounded-lg border disabled:opacity-40">Mais recentes</button>
            <button disabled={pagina >= totalPag} onClick={() => setPagina(p => p + 1)} className="h-8 px-3 rounded-lg border disabled:opacity-40">Mais antigos</button>
          </div>
        </div>
      )}
    </div>
  );
}
